import { useRef, useState } from "react";
import api from "@/lib/api";
import { API } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { KeyRound, Camera, MapPin, ShieldAlert, CheckCircle2, Loader2, Copy, Mail, X, Download } from "lucide-react";
import { toast } from "sonner";

// Steps: capture -> location -> confirm -> done
export function SurrenderFlow({ propertyId, address, open, onOpenChange, onDone }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState("capture");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState(null);

  const reset = () => {
    setStep("capture"); setFile(null); setPreview(null); setCoords(null);
    setLocating(false); setSubmitting(false); setReceiptUrl(null);
  };

  const handleClose = (v) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const requestLocation = () => {
    if (!navigator.geolocation) { toast.error("Location isn't available on this device."); setStep("confirm"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy }); setLocating(false); setStep("confirm"); },
      () => { setLocating(false); toast.message("No location added — that's okay, it's optional."); setStep("confirm"); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (coords) {
        fd.append("client_latitude", coords.lat);
        fd.append("client_longitude", coords.lng);
        if (coords.acc != null) fd.append("client_accuracy", coords.acc);
      }
      fd.append("client_captured_at", new Date().toISOString());
      const { data } = await api.post(`/properties/${propertyId}/surrender`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReceiptUrl(data.receipt_url);
      setStep("done");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't complete move-out. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(receiptUrl);
      else throw new Error();
      toast.success("Receipt link copied");
    } catch {
      const ta = document.createElement("textarea");
      ta.value = receiptUrl; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      let ok = false; try { ok = document.execCommand("copy"); } catch { ok = false; }
      document.body.removeChild(ta);
      ok ? toast.success("Receipt link copied") : toast.error("Couldn't copy — please try again.");
    }
  };

  const mailtoHref = receiptUrl
    ? `mailto:?subject=${encodeURIComponent("Keys surrendered — move-out documented")}&body=${encodeURIComponent(
        `Hello,\n\nI've returned the keys and documented the move-out. You can view the surrender receipt (key photo + timestamp) here:\n${receiptUrl}\n\nThank you.`
      )}`
    : "#";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-2xl" data-testid="surrender-flow">
        {step === "capture" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-navy flex items-center gap-2"><KeyRound size={20} className="text-brand" /> Photograph the keys</DialogTitle>
              <DialogDescription>Take a clear photo of the keys where you're leaving them (mailbox, lockbox, counter, or handed over).</DialogDescription>
            </DialogHeader>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="surrender-file-input" onChange={onPick} />
            {preview ? (
              <img src={preview} alt="Keys preview" className="w-full max-h-72 object-contain rounded-xl border border-[#E2E8F0] bg-[#F8FAFC]" data-testid="surrender-preview" />
            ) : (
              <button onClick={() => fileRef.current?.click()} data-testid="surrender-capture-btn"
                className="w-full rounded-xl border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] py-10 grid place-items-center text-[#64748B] hover:border-brand hover:text-brand transition-colors">
                <Camera size={28} /><span className="mt-2 text-sm font-semibold">Photograph keys</span>
              </button>
            )}
            <div className="mt-2 flex justify-between gap-2">
              {preview && <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-xl" data-testid="surrender-retake-btn">Retake</Button>}
              <Button disabled={!file} onClick={() => setStep("location")} data-testid="surrender-next-btn"
                className="ml-auto rounded-xl bg-brand hover:bg-brand-hover text-white disabled:opacity-50">Next</Button>
            </div>
          </>
        )}

        {step === "location" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-navy flex items-center gap-2"><MapPin size={20} className="text-brand" /> Add location?</DialogTitle>
              <DialogDescription>Optional — adds where the keys were surrendered to your receipt. We only use it for this receipt.</DialogDescription>
            </DialogHeader>
            {coords && (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-800 flex items-center gap-2" data-testid="surrender-coords">
                <CheckCircle2 size={15} /> Location added ({coords.lat.toFixed(4)}, {coords.lng.toFixed(4)})
              </div>
            )}
            <div className="mt-2 flex justify-between gap-2">
              <Button variant="ghost" onClick={() => setStep("confirm")} data-testid="surrender-skip-location-btn" className="rounded-xl text-[#64748B]">Skip</Button>
              <Button onClick={requestLocation} disabled={locating} data-testid="surrender-add-location-btn"
                className="rounded-xl bg-brand hover:bg-brand-hover text-white">
                {locating ? <><Loader2 size={16} className="mr-2 animate-spin" /> Locating…</> : <><MapPin size={16} className="mr-2" /> Add my location</>}
              </Button>
            </div>
          </>
        )}

        {step === "confirm" && (
          <>
            <DialogHeader>
              <DialogTitle className="font-heading text-navy flex items-center gap-2"><ShieldAlert size={20} className="text-amber-ink" /> Confirm move-out</DialogTitle>
              <DialogDescription>This permanently locks this property's records. You won't be able to add or edit photos, rooms, or details afterward.</DialogDescription>
            </DialogHeader>
            <div className="rounded-xl bg-amber-soft border border-amber-border px-4 py-3 text-sm text-amber-ink" data-testid="surrender-confirm-warning">
              A timestamped key photo will be saved{coords ? " with your location" : ""}, the property will be sealed, and a shareable receipt created for your landlord.
            </div>
            <div className="mt-2 flex justify-between gap-2">
              <Button variant="outline" onClick={() => setStep("location")} className="rounded-xl" data-testid="surrender-back-btn">Back</Button>
              <Button onClick={submit} disabled={submitting} data-testid="surrender-confirm-btn"
                className="rounded-xl bg-navy hover:bg-navy-hover text-white">
                {submitting ? <><Loader2 size={16} className="mr-2 animate-spin" /> Finalizing…</> : "Complete move-out"}
              </Button>
            </div>
          </>
        )}

        {step === "done" && (
          <div className="text-center py-2" data-testid="surrender-success">
            <div className="mx-auto grid place-items-center h-14 w-14 rounded-full bg-emerald-100 text-emerald-600"><CheckCircle2 size={30} /></div>
            <h3 className="mt-4 font-heading text-xl font-bold text-navy">Move-out complete ✓</h3>
            <p className="mt-1 text-sm text-[#64748B]">Receipt ready to send to your landlord.</p>
            <div className="mt-5 flex flex-col gap-2">
              <Button onClick={copyLink} data-testid="surrender-copy-link-btn" className="rounded-xl bg-brand hover:bg-brand-hover text-white">
                <Copy size={16} className="mr-2" /> Copy receipt link
              </Button>
              <a href={mailtoHref} data-testid="surrender-email-btn">
                <Button variant="outline" className="w-full rounded-xl border-[#E2E8F0]"><Mail size={16} className="mr-2" /> Email to landlord</Button>
              </a>
              {receiptUrl && (
                <a href={`${API}/surrender/${receiptUrl.split("/").pop()}/receipt.pdf`} target="_blank" rel="noopener noreferrer" data-testid="surrender-download-pdf-btn">
                  <Button variant="outline" className="w-full rounded-xl border-[#E2E8F0]"><Download size={16} className="mr-2" /> Download receipt (PDF)</Button>
                </a>
              )}
              <Button variant="ghost" onClick={() => handleClose(false)} data-testid="surrender-done-btn" className="rounded-xl text-[#64748B]">Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
